/**
 * @fileoverview Advanced searcher for full-text and expression-based search.
 * @module searchers/AdvancedSearcher
 */

import { DbSearcher } from './DbSearcher.js';
import { buildEmptySearchResultStructure, includesAtStartOfWord, includesAtStartOfWordExceptFields } from '../utils/jrfuncs.js';
import {
    preProcessQueryParseOptionsDirectives,
    preProcessQueryCheckAdvanced,
    compileAdvancedFiltrexQuery,
    testAdvancedQueryMatchOnItem,
    simplifyErrorMessage,
    buildContextDictionary,
    clearCustomFunctionDataCache
} from '../utils/jrdslquery.js';
import { DEBUG_CBM, SEARCH_TYPES, DOM_ELEMENTS, ADVANCED_SEARCH_FIELDS, ADVANCED_SEARCH_FIELDS_ANYWHERE, ADVANCED_SEARCH_FIELDS_STRINGSTART, MAPAPP_DEFAULTS } from '../constants.js';

const { ID_ADVANCED_RESTRICT_REGION_CHECKBOX, CLASS_ADVANCED_REGION_CHECKBOX_ROW, ID_ADVANCED_SORT_BY_ANCHOR_DISTANCE_CHECKBOX, CLASS_ADVANCED_ANCHOR_DISTANCE_CHECKBOX_ROW } = DOM_ELEMENTS;
import { parseBlockQueryString } from '../utils/nynfuncs.js';

/**
 * Manages Advanced search functionality.
 * Supports full-text substring search and Filtrex expression language.
 * @class
 * @extends DbSearcher
 */
export class AdvancedSearcher extends DbSearcher {
    /**
     * Creates a new AdvancedSearcher instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {Object} [options={}] - Configuration options
     */
    constructor(mapApp, options = {}) {
        super(mapApp, {
            searcherName: SEARCH_TYPES.ADVANCED,
            searchMethod: AdvancedSearcher.prototype.DoAdvancedSearchDataUsingAdvancedQueryLanguageOrWildcard,
            searchMethodOptions: { fields: ADVANCED_SEARCH_FIELDS },
            resultLimitEnterKey: MAPAPP_DEFAULTS.RESULT_LIMIT_DEFAULT_INITIAL_DB_SEARCH_ADVANCED_ENTER,
            minQueryLength: 2,
            enableActionBarSave: true,
            enableActionBarEdit: true,
            enableActionBarRead: true,
            alwaysShowExtraPcatInfo: true,
            emptyMessage: 'Enter an advanced search query.<br/><br/>Remember that the Advanced Search tab is for case authors; using it as a player is considered cheating.',
            supportsAnchorSort: true,
            listerOptions: { 
                contentsLabel: 'advanced search results',
                supportsIndent: true,
            },
            ...options
        });

        // wire click handlers (the checkboxes are static page DOM: destroy() removes these again)
        this.checkboxListenerRemovers = [];
        this.setupClickHandlersForRegionCheckbox();
        this.setupClickHandlersForAnchorDistanceCheckbox();
    }


    /**
     * Destroys the searcher, removing the listeners this searcher put on the static checkboxes.
     * @override
     */
    destroy() {
        for (const remove of this.checkboxListenerRemovers) {
            remove();
        }
        this.checkboxListenerRemovers = [];
        super.destroy();
    }



    // ==========================================
    // SETUP
    // ==========================================

    /**
     * Initializes state-dependent behavior after StateManager is loaded.
     * Restores the restrict-to-region and sort-by-anchor-distance checkbox states.
     * @override
     */
    initAfterStateLoaded() {
        // Load saved state from StateManager
        const savedValue = this.mapApp.getStateManager().getRestrictSearchToRegion();
        this.setRestrictSearchToRegion(savedValue, false);
        // Update checkbox visibility/enabled based on if their is a region selected
        this.updateRestrictRegionCheckboxEnabledState()

        // Load saved sort by anchor distance state
        const savedAnchorDistValue = this.mapApp.getStateManager().getSortByAnchorDistance();
        this.setSortByAnchorDistance(savedAnchorDistValue, false);
        // Update checkbox visibility based on if there is an anchor set
        this.updateSortByAnchorDistanceCheckboxEnabledState();
    }







    // ==========================================
    // Restrict to Region Checkbox
    // ==========================================

    /**
     * Initializes the restrict-to-region checkbox with saved state and event handlers.
     */
    setupClickHandlersForRegionCheckbox() {
        // Handle checkbox changes
        const checkbox = document.getElementById(ID_ADVANCED_RESTRICT_REGION_CHECKBOX);
        const onChange = () => {
            this.mapApp.getStateManager().setRestrictSearchToRegion(checkbox.checked);
            // Re-perform this advanced search if we have an active query
            if (this.getInputValue() && this.doesSearchDependOnSelectedRegion()) {
                this.rePerformSearchToFullyRefreshResultDisplay(true);
            }
        };
        checkbox.addEventListener('change', onChange);
        this.checkboxListenerRemovers.push(() => checkbox.removeEventListener('change', onChange));
    }



    /**
     * Updates the visibility of the restrict-to-region checkbox row.
     * Row is only visible when a region is selected.
     */
    updateRestrictRegionCheckboxEnabledState() {
        const row = document.querySelector('.' + CLASS_ADVANCED_REGION_CHECKBOX_ROW);
        if (!row) return;

        const hasRegion = !!this.mapApp.getSelectedRegion();
        row.style.display = hasRegion ? '' : 'none';
    }

    /**
     * Gets whether the search should be restricted to the selected region.
     * @returns {boolean} True if search should be restricted to selected region
     */
    getRestrictSearchToRegion() {
        const checkbox = document.getElementById(ID_ADVANCED_RESTRICT_REGION_CHECKBOX);
        if (!checkbox) return false;
        const row = document.querySelector('.' + CLASS_ADVANCED_REGION_CHECKBOX_ROW);
        const isVisible = row && row.style.display !== 'none';
        return checkbox.checked && isVisible;
    }

    /**
     * Sets the restrict-to-region checkbox state.
     * @param {boolean} value - Whether to restrict search to selected region
     */
    setRestrictSearchToRegion(value, flagSaveState=true) {
        const checkbox = document.getElementById(ID_ADVANCED_RESTRICT_REGION_CHECKBOX);
        if (checkbox) {
            // set it
            checkbox.checked = value;
            //
            if (flagSaveState) {
                this.mapApp.getStateManager().setRestrictSearchToRegion(value);
            }
        }
    }

    /**
     * Called by MapApp when the global region selection changes.
     * Updates checkbox enabled state and re-performs search if needed.
     */
    onRegionChanged() {
        // Update checkbox enabled state
        this.updateRestrictRegionCheckboxEnabledState();
        // Re-perform this advanced search if we have an active query and search depends on region
        if (this.getInputValue() && this.doesSearchDependOnSelectedRegion()) {
            if (!this.ignoreRegionChangeEvents) {
                this.rePerformSearchToFullyRefreshResultDisplay(true);
            }
        }
    }




    // ==========================================
    // Sort by Anchor Distance Checkbox
    // ==========================================

    /**
     * Initializes the sort-by-anchor-distance checkbox with event handlers.
     */
    setupClickHandlersForAnchorDistanceCheckbox() {
        const checkbox = document.getElementById(ID_ADVANCED_SORT_BY_ANCHOR_DISTANCE_CHECKBOX);
        const onChange = () => {
            this.mapApp.getStateManager().setSortByAnchorDistance(checkbox.checked);
            // Re-perform this advanced search if we have an active query
            if (true || this.getInputValue()) {
                this.reSearchOrReSortAfterAnchorDistanceOrSortOptionChanges();
            }
        };
        checkbox.addEventListener('change', onChange);
        this.checkboxListenerRemovers.push(() => checkbox.removeEventListener('change', onChange));
    }



    /**
     * Updates the visibility of the sort-by-anchor-distance checkbox row.
     * Row is only visible when an anchor is set.
     */
    updateSortByAnchorDistanceCheckboxEnabledState() {
        const row = document.querySelector('.' + CLASS_ADVANCED_ANCHOR_DISTANCE_CHECKBOX_ROW);
        if (!row) return;

        const hasAnchor = !!this.mapApp.currentAnchor.uid;
        row.style.display = hasAnchor ? '' : 'none';
    }

    /**
     * Gets whether results should be sorted by distance from anchor.
     * @returns {boolean} True if sort by anchor distance is enabled and visible
     */
    getSortByAnchorDistance() {
        const checkbox = document.getElementById(ID_ADVANCED_SORT_BY_ANCHOR_DISTANCE_CHECKBOX);
        if (!checkbox) return false;
        const row = document.querySelector('.' + CLASS_ADVANCED_ANCHOR_DISTANCE_CHECKBOX_ROW);
        const isVisible = row && row.style.display !== 'none';
        return checkbox.checked && isVisible;
    }

    /**
     * Sets the sort-by-anchor-distance checkbox state.
     * @param {boolean} value - Whether to sort by anchor distance
     * @param {boolean} [flagSaveState=true] - Whether to persist the state
     */
    setSortByAnchorDistance(value, flagSaveState=true) {
        const checkbox = document.getElementById(ID_ADVANCED_SORT_BY_ANCHOR_DISTANCE_CHECKBOX);
        if (checkbox) {
            checkbox.checked = value;
            if (flagSaveState) {
                this.mapApp.getStateManager().setSortByAnchorDistance(value);
            }
        }
    }

    /**
     * Called by MapApp when the anchor changes.
     * Updates checkbox visibility and re-performs search if needed.
     */
    onAnchorChanged() {
        this.updateSortByAnchorDistanceCheckboxEnabledState();
        //
        // Re-perform search if we have an active query and sort by distance is enabled
        if (true || (this.getInputValue() && this.getSortByAnchorDistance())) {
            this.reSearchOrReSortAfterAnchorDistanceOrSortOptionChanges();
        }
    }













    // ==========================================
    // SORT STUFF
    // ==========================================


    /**
     * Re-sorts or re-searches results after anchor distance or sort option changes.
     * Sorts by anchor distance if enabled, otherwise restores default sort order.
     * @private
     */
    reSearchOrReSortAfterAnchorDistanceOrSortOptionChanges() {
        // ATTN: note that currently we dont pre-filter by distance so that means that we can just re-sort not re-search the db
        // console.log('ATTN: IN reSearchOrReSortAfterAnchorDistanceOrSortOptionChanges')
        const shouldSortByAnchorDist = this.getMapApp().calcShouldSortByAnchorDist();
        if (shouldSortByAnchorDist) {
            this.sortResultsByAnchorDistance();
        } else {
            this.sortResultsByDefault();
        }
        //this.rePerformSearchToFullyRefreshResultDisplay(true);
    }






    // ==========================================
    // DERIVED CLASS SEARCH HOOKS
    // ==========================================

    /**
     * Injects selected region and region checkbox state into the search pack
     * before executing the database search.
     * @param {Object} searchPack - The search pack to modify
     * @override
     */
    performDbPreSearchOptionsModifications(searchPack) {
        searchPack.selectedRegion = this.mapApp.getSelectedRegion();
        searchPack.regionCheckbox = this.mapApp.getSelectedRegionFilterCheckbox();
    }





    // ==========================================
    // Search Methods
    // ==========================================


    /**
     * Searches using either advanced query language (Filtrex expressions) or simple substring matching.
     * Determines search mode based on query syntax, handles region restriction, and defers
     * advanced expression searches until Enter key is pressed.
     * @param {Object} searchPack - The search pack with query, isEnterKey, and options
     * @returns {Promise<Object>} Search result object with results array, totalCount, and optional error
     */
    async DoAdvancedSearchDataUsingAdvancedQueryLanguageOrWildcard(searchPack) {
        let isAdvanced, error;
        const optionDontRestrictRegionIfSelectedRegionUsedExplicitlyInExpression = true;

        this.clearSortOverride();

        // strip off directives
        error = preProcessQueryParseOptionsDirectives(searchPack);
        if (error) return buildEmptySearchResultStructure(error);

        if (this.handleSecretAdvancedSearch(searchPack)) {
            return null;
        }

        // figure out if this is an advanced search and do any pre-processing if so
        error = preProcessQueryCheckAdvanced(searchPack);
        if (error) return buildEmptySearchResultStructure(error);

        // destructure after checking is this an advanced search?
        isAdvanced = searchPack.isAdvanced;

        // clear
        delete searchPack.waitingOnEnter;

        // IF user says they want to filter by region
        const selectedRegion = this.mapApp.getSelectedRegion();
        if (this.getRestrictSearchToRegion()) {
            // ok now that we have parsed directives, we will force in our selectedRegion option if no region was specified manually, so IT will act as a filter
            // NOTE we also check for hasSelectedRegionExpression which should be set by our advanced parser and tells us whether selectedRegion is being used in an expression and so should NOT be used as filter
            if (!searchPack.jregion && (!optionDontRestrictRegionIfSelectedRegionUsedExplicitlyInExpression || !searchPack.hasSelectedRegionExpression) && selectedRegion) {
                searchPack.jregion = selectedRegion;
            }
        }

        if (isAdvanced) {
            if (searchPack.isEnterKey) {
                // show loading right before real work
                await this.showLoading();
                // run the search
                return this.searchDataUsingAdvancedQueryLanguage(searchPack);
            } else {
                // dont run the search because enter not pressed
                searchPack.waitingOnEnter = true;
                return buildEmptySearchResultStructure();
            }
        } else {
            // run normal search
            // show loading right before real work?
            // await this.showLoading();
            //
            // first check if its region-type search
            const { jregion, loclabel } = parseBlockQueryString(searchPack.query);
            if (jregion && loclabel) {
                // it may be, so run it like a reverse search -- but keep that answer only if the block has
                // something in it: an ordinal street name reads as a block too ("84th" = block 84 of a
                // region "TH"), and used to answer 0 hits instead of searching the text
                this.setSortOverride('sortAddress');
                const retv = await this.getMapApp().reverseSearcher.searchDataForJregionLoc(jregion, loclabel, searchPack);
                if (retv.totalCount > 0) {
                    return retv;
                }
                this.clearSortOverride();
            }

            // generic substring search
            const query = searchPack.query;
            parseBlockQueryString(query);
            //return this.searchDataForSubStringAnywhere(searchPack);
            return this.searchDataForSubStringAtStartOfWords(searchPack);
        }
    }







    /**
     * Searches for items where any specified field contains the query as a substring.
     * @param {Object} searchPack - The search pack with query and options
     * @returns {Object} Search result object with results array and totalCount
     */
    searchDataForSubStringAnywhere(searchPack) {
        // Note that we have already stripped off directives and this called funciton will do so again, but thats ok that will have no effect
        // console.log('DEBUG: In searchDataForSubStringAnywhere "${JSON.stringify(searchPack)}"')
        const query = searchPack.query.toLowerCase().trim();
        if (query.includes(' ')) {
            // special match against all space separated words
            const wordList = query.split(' ');
            const matchFn = (value, word, field) => value.includes(word);
            return this.searchDataWithWordsAgainstFields(searchPack, matchFn, wordList);
        } else {
            // just search for one word in field values
            const matchFn = (value, searchPack, field) => value.includes(searchPack.query);
            return this.searchDataWithFieldMatchFunction(searchPack, matchFn);
        }
    }



    /**
     * Searches for items where any specified field contains the query at the start of a word
     * (at the start of the field text, or after whitespace/punctuation), e.g. "smi" finds "Smith" but not "Blacksmith".
     * @param {Object} searchPack - The search pack with query and options
     * @returns {Object} Search result object with results array and totalCount
     */
    searchDataForSubStringAtStartOfWords(searchPack) {
        // Note that we have already stripped off directives and this called funciton will do so again, but thats ok that will have no effect
        // console.log('DEBUG: In searchDataForSubStringAtStartOfWords "${JSON.stringify(searchPack)}"')
        const fullSearchFields = ADVANCED_SEARCH_FIELDS_ANYWHERE;
        const startStringSearchFields = ADVANCED_SEARCH_FIELDS_STRINGSTART;
        //
        const query = searchPack.query.toLowerCase().trim();
        if (query.includes(' ')) {
            // special match against all space separated words
            const wordList = query.split(' ');
            const matchFn = (value, word, field) => includesAtStartOfWordExceptFields(value, word, field, fullSearchFields, startStringSearchFields);
            return this.searchDataWithWordsAgainstFields(searchPack, matchFn, wordList);
        } else {
            // just search for one word in field values
            const matchFn = (value, searchPack, field) => includesAtStartOfWordExceptFields(value, searchPack.query, field, fullSearchFields, startStringSearchFields);
            return this.searchDataWithFieldMatchFunction(searchPack, matchFn);
        }
    }




    /**
     * Searches using advanced query language (Filtrex expression).
     * Compiles the query, builds the context dictionary, and uses
     * searchDataWithItemMatchFunction for the iteration logic.
     * @param {Object} searchPack - The search pack with compiled query and options
     * @returns {Object} Search result object with results array, totalCount, and optional error
     */
    searchDataUsingAdvancedQueryLanguage(searchPack) {
        const filtrexCompiled = compileAdvancedFiltrexQuery(searchPack, this);
        if (filtrexCompiled.error) {
            const error = simplifyErrorMessage(filtrexCompiled.error);
            const msg = 'Query syntax error:\n' + error;
            //queueDelayedSearchWarning(msg);
            return buildEmptySearchResultStructure(msg);
        }

        if (DEBUG_CBM) {
            console.debug(`DEBUG: In searchDataUsingAdvancedQueryLanguage with filtrexCompiled = "${JSON.stringify(filtrexCompiled)}"`)
        }

        // assemble context dictionary (injects custom variables)
        const context = buildContextDictionary(searchPack);

        // clear the cache that we may use
        clearCustomFunctionDataCache();

        return this.searchDataWithItemMatchFunction(
            searchPack,
            (item) => testAdvancedQueryMatchOnItem(filtrexCompiled, item, context)
        );
    }








    // ==================== 
    // CAN BE SUBLASSES

    /**
     * Clears all search input elements for the advanced searcher.
     * Region selection is handled separately by RegionControl on the map.
     * @override
     */
    clearAllSearchInputs() {
        // clear the search input (region is now handled by RegionControl on the map)
        super.clearAllSearchInputs();
    }
    // ====================








    // ==================== 
    // NEW SEARCH BACK STUFF
    // ==================== 

    /**
     * Sets input element values from a search pack object.
     * Overrides base to also restore the selected region if present in the search pack.
     * @param {Object} searchPack - The search pack containing query, selectedRegion, and regionCheckbox
     * @override
     */
    setInputValuesFromSearchPack(searchPack) {
        // set input value
        this.setInputValue(searchPack.query);
        // If the saved search has a region, set it via MapApp's global region control
        if (searchPack.selectedRegion) {
            this.mapApp.setSelectedRegionProgrammatically(searchPack.selectedRegion, searchPack.regionCheckbox);
        }
    }



    /**
     * Returns true since the advanced searcher supports region-based filtering.
     * @returns {boolean} True
     * @override
     */
    doesSearchDependOnSelectedRegion() {
        return true;
    }



    /**
     * Sorts results for advanced search display by name.
     * @param {Array} results - The results to sort
     * @returns {Array} The sorted results
     * @override
     */
    sortResults(results) {
        // Override in subclasses if needed
        const sortOverride = this.getSortOverride();
        if (sortOverride=='sortAddress') {
            return this.sortResultsByField(results, 'sort_a', 'address');
        }
        return this.sortResultsByField(results, 'sort_n', 'dName');
    }




    // ==========================================
    // Post-Search Operations
    // ==========================================

    /**
     * Performs operations after completing a search.
     * Handles history saving based on search type and user action.
     * Saves immediately on Enter key; schedules delayed save otherwise.
     * @param {Object} searchPack - The search pack with query, isEnterKey, flagUserInitiatedThisSearch, explicitLimit, totalCount
     * @override
     */
    performPostSearchOperations(searchPack) {
        // destructure searchPack
        const {query, isEnterKey, flagUserInitiatedThisSearch, explicitLimit, totalCount} = searchPack;
        if (isEnterKey && flagUserInitiatedThisSearch) {
            // first check if its region-type search
            const { jregion, loclabel } = parseBlockQueryString(searchPack.query);
            const results = this.getListerResults();
            // fit the extent to the visible items -- only when there are some (an empty set would
            // fall back to the region or initial bounds and reset the view)
            if (jregion && loclabel && results && results.length > 0) {
                this.getMapApp().autoZoomPanToResultSet(results);
            }
        }

        // call super class
        super.performPostSearchOperations(searchPack);
    }



    handleSecretAdvancedSearch(searchPack) {
        const isEnterKey = searchPack.isEnterKey
        const query = searchPack.query;
        if (!isEnterKey) {
            return false;
        }
        // return ture IFF this query is some advanced query
        if (query=="debugAddressPoints") {
            this.handleSecretAdvancedSearch_DebugAddressPoints()
            return true;
        }
    }


    handleSecretAdvancedSearch_DebugAddressPoints() {
        // helper function to show labeled address points on map; this is useful for debugging adding a new game world like Mythos Tales, SHCD, BOI, etc.
        // ask map to label every unique jregion+loclabel
        this.mapApp.debugLabelAllUniqueLocLabelsOnMap();
    }

}
